import { useRef, useCallback, useState, useEffect } from 'react'
import { C } from '../landing.theme'
import { Ic } from '../ui/Ic'
import { LandingPoolPreview } from '../LandingPoolPreview'

const BENTO_POOL_FIN = { pictures: [null, '/texture/mosaic.webp'] }
const BENTO_POOL_EQ  = { light: true, ladder: true, embed: true }

function BentoPool() {
  const ref = useRef(null)
  // ready: Canvas смонтирован (WebGL контекст создан)
  // inView: раздел виден — анимация включена
  const [ready, setReady] = useState(false)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // IntersectionObserver #1: включаем/выключаем анимационный цикл
    const visOb = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0, rootMargin: '50px 0px' }
    )
    visOb.observe(el)

    // IntersectionObserver #2: крайний случай — если юзер быстро доскроллил до секции
    // раньше чем сработал requestIdleCallback, монтируем Canvas при 150px до экрана
    const mountOb = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setReady(true); mountOb.disconnect() } },
      { threshold: 0, rootMargin: '150px 0px' }
    )
    mountOb.observe(el)

    // Основной способ: монтируем Canvas когда браузер простаивает (не во время скролла).
    // После загрузки Hero браузер освобождается → requestIdleCallback срабатывает →
    // buildPoolGeometries выполняется в idle-время, не блокируя прокрутку.
    let ricId = null
    if (typeof requestIdleCallback !== 'undefined') {
      ricId = requestIdleCallback(() => setReady(true), { timeout: 3000 })
    } else {
      ricId = setTimeout(() => setReady(true), 1800)
    }

    return () => {
      visOb.disconnect()
      mountOb.disconnect()
      if (ricId != null) {
        if (typeof cancelIdleCallback !== 'undefined') cancelIdleCallback(ricId)
        else clearTimeout(ricId)
      }
    }
  }, [])

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0 }}>
      {ready && (
        <LandingPoolPreview
          len={4} wid={3} dep={1.5}
          finObj={BENTO_POOL_FIN}
          eq={BENTO_POOL_EQ}
          autoRotate
          autoRotateSpeed={1.2}
          enableRotate={false}
          frameloop={inView ? 'always' : 'never'}
          instant
          figDescription="3D визуализация бассейна 4×3 м с мозаичной отделкой, RGB прожекторами, лестницей и закладными элементами - демонстрация возможностей сервиса Welland Create для подбора оборудования бассейна онлайн."
        />
      )}
    </div>
  )
}

function TiltCard({ children, className, style }) {
  const ref = useRef(null)
  const raf = useRef(null)
  const onMove = useCallback(e => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const x = (e.clientX - r.left) / r.width - 0.5
    const y = (e.clientY - r.top) / r.height - 0.5
    if (!raf.current) {
      raf.current = requestAnimationFrame(() => {
        el.style.transform = `perspective(700px) rotateX(${y * -9}deg) rotateY(${x * 9}deg) translateZ(6px)`
        raf.current = null
      })
    }
  }, [])
  const onLeave = useCallback(() => {
    if (raf.current) { cancelAnimationFrame(raf.current); raf.current = null }
    if (ref.current) ref.current.style.transform = 'perspective(700px) rotateX(0deg) rotateY(0deg) translateZ(0)'
  }, [])
  return (
    <div ref={ref} className={className} style={{ ...style, willChange: 'transform' }}
      onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  )
}

export default function Features() {
  const secRef = useRef(null)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    const el = secRef.current
    if (!el) return
    // Наблюдаем за секцией целиком — срабатывает за 160px до попадания в экран
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); ob.disconnect() } },
      { threshold: 0, rootMargin: '160px 0px' }
    )
    ob.observe(el)
    return () => ob.disconnect()
  }, [])

  return (
    <section ref={secRef} className="sec" id="features" style={{ background: C.paper }}>
      {/* Весь контент анимируется как один блок — одна CSS transition, не перегружает систему */}
      <div className="wrap" style={{
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'none' : 'translateY(20px)',
        transition: 'opacity .38s ease, transform .42s cubic-bezier(.2,.7,.2,1)',
      }}>
        <div className="sec-head">
          <span className="eb light">ВОЗМОЖНОСТИ</span>
          <h2 className="chrome-ink" style={{ fontSize: 'clamp(26px,4vw,42px)', marginTop: 16 }}>
            Всё для правильного выбора<br/>оборудования
          </h2>
          <p style={{ color: C.ink2, fontSize: 16.5, lineHeight: 1.65, marginTop: 14, maxWidth: 540, margin: '14px auto 0' }}>
            От размеров бассейна до PDF-спецификации - за минуты.
          </p>
        </div>

        <div className="bento">

          {/* Hero card — реальный 3D бассейн */}
          <TiltCard className="bento-hero">
            <BentoPool/>

            <div style={{ position: 'absolute', top: 20, left: 24, zIndex: 2, pointerEvents: 'none' }}>
              <span className="eb">ГЛАВНАЯ ФУНКЦИЯ</span>
            </div>

            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '64px 26px 26px', background: 'linear-gradient(to bottom,transparent,rgba(3,8,18,.96) 55%)', zIndex: 2, pointerEvents: 'none' }}>
              <h3 style={{ fontSize: 20, color: '#fff', marginBottom: 8, lineHeight: 1.25 }}>3D-визуализация в реальном времени</h3>
              <p style={{ fontSize: 14, lineHeight: 1.62, color: C.s2 }}>Меняйте размеры, отделку и оборудование - бассейн обновляется мгновенно. Крутите модель на 360°.</p>
            </div>
          </TiltCard>

          {/* Умный подбор */}
          <TiltCard className="bento-cell">
            <div style={{ padding: 26, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(10,123,134,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <span style={{ color: '#0A7B86' }}><Ic n="cog" s={22}/></span>
              </div>
              <h3 style={{ fontSize: 16, marginBottom: 7, color: C.ink, lineHeight: 1.3 }}>Умный подбор</h3>
              <p style={{ fontSize: 14, lineHeight: 1.62, color: C.ink2 }}>Система рассчитывает нужный насос, фильтр и теплообменник автоматически - по объёму и типу бассейна.</p>
            </div>
          </TiltCard>

          {/* Актуальные цены */}
          <TiltCard className="bento-cell">
            <div style={{ padding: 26, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(10,123,134,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <span style={{ color: '#0A7B86' }}><Ic n="ruler" s={22}/></span>
              </div>
              <h3 style={{ fontSize: 16, marginBottom: 7, color: C.ink, lineHeight: 1.3 }}>Актуальные цены</h3>
              <p style={{ fontSize: 14, lineHeight: 1.62, color: C.ink2 }}>Стоимость комплекта пересчитывается мгновенно. Только реальные позиции со склада - без устаревших прайсов.</p>
            </div>
          </TiltCard>

          {/* PDF-спецификация */}
          <TiltCard className="bento-cell">
            <div style={{ padding: 26, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(10,123,134,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <span style={{ color: '#0A7B86' }}><Ic n="doc" s={22}/></span>
              </div>
              <h3 style={{ fontSize: 16, marginBottom: 7, color: C.ink, lineHeight: 1.3 }}>PDF-спецификация</h3>
              <p style={{ fontSize: 14, lineHeight: 1.62, color: C.ink2 }}>Полный перечень с артикулами, характеристиками и ценами. Готов для монтажников и выставления счёта.</p>
            </div>
          </TiltCard>

          {/* Проверенные бренды */}
          <TiltCard className="bento-cell">
            <div style={{ padding: 26, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(10,123,134,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <span style={{ color: '#0A7B86' }}><Ic n="shield" s={22}/></span>
              </div>
              <h3 style={{ fontSize: 16, marginBottom: 7, color: C.ink, lineHeight: 1.3 }}>Проверенные бренды</h3>
              <p style={{ fontSize: 14, lineHeight: 1.62, color: C.ink2 }}>Pahlen, Emaux, Landy, CGT, Reviglass и другие производители. Только сертифицированное оборудование.</p>
            </div>
          </TiltCard>

        </div>
      </div>
    </section>
  )
}